import React, { useState, useEffect } from "react";
import { Accordion, Button, Container, Row, Col, ListGroup, Badge, Spinner, Tabs, Tab } from "react-bootstrap";
import AttachmentPreview from "./AttachmentPreview";
import { formatLocalDate } from "../utils/dateUtils";
import { getIconForContentType } from "../utils/getIconForContentType";
import { getResourceIcon } from "../utils/renderUtils";
import attachmentStyles from "./AttachmentBrowser.module.css";
import ResourceViewerModal from "../modals/ResourceViewerModal";
import ObservationIcon from "../assets/microscope_96.png";
import DiagnosticReportIcon from "../assets/diagnostic-report-1.png";
import DocumentReferenceIcon from "../assets/doc-ref-1.png";
import CarePlanIcon from "../assets/care-plan-1.png";
import ConditionIcon from "../assets/condition-1.png";
import MediaIcon from "../assets/media-1.png";
import SpecimenIcon from "../assets/specimen-1.png";
import QuestionnaireResponseIcon from "../assets/question-1.png";
import { resolveName } from "../utils/messageUtils";

const getExpandedIconFor = (resourceType) => {
  switch (resourceType) {
    case "Observation":
      return ObservationIcon;

    case "DiagnosticReport":
      return DiagnosticReportIcon;

    case "DocumentReference":
      return DocumentReferenceIcon;

    case "Media":
      return MediaIcon;

    case "Specimen":
      return SpecimenIcon;

    case "CarePlan":
      return CarePlanIcon;

    case "Condition":
      return ConditionIcon;

    case "QeustionnaireResponse":
      return QuestionnaireResponseIcon;

    default:
      return ObservationIcon; // fallback
  }
};

const pluralMap = {
  Media: "Media",
  Observation: "Observations",
  DocumentReference: "Document References",
  DiagnosticReport: "Diagnostic Reports",
  Specimen: "Specimens",
  CarePlan: "Care Plans",
  QuestionnaireResponse: "Questionnaire Responses"
};

const pluralize = (type) => pluralMap[type] || `${type}s`;
const truncate = (str, maxLength = 25) =>
  str.length > maxLength ? str.slice(0, maxLength) + "…" : str;

const getResourceDate = (entry) => {
  const dateStr =
    entry.resource?.effectiveDateTime ||
    entry.resource?.issued ||
    entry.resource?.date ||
    null;
  return dateStr ? new Date(dateStr) : new Date(0);
};

const AttachmentBrowser = ({ selectedPatient, fhirClient, onAttach, onClose }) => {
  const patientId = selectedPatient?.id;
  const [entries, setEntries] = useState([]);
  const [localFiles, setLocalFiles] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedAttachments, setSelectedAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("resources");
  const [showModal, setShowModal] = useState(false);
  const [modalResource, setModalResource] = useState(null);
  const [modalResourceDetails, setModalResourceDetails] = useState(null);
  const [modalIcon, setModalIcon] = useState(null);

  const handleModalClose = () => {
    setShowModal(false);
    setModalResource(null);
  };

  const openGenericViewer = (resource, icon) => {
    setModalResourceDetails(resource);
    setModalIcon(icon);
    setShowModal(true);
  };


  const fetchEntries = async () => {
    if (!patientId || !fhirClient) return;
    setLoading(true);
    const results = [];
    const resourceTypes = ["DocumentReference", "DiagnosticReport", "Media", "Observation", "Specimen", "CarePlan", "Condition", "QuestionnaireResponse"];

    const fetchers = resourceTypes.map(async (type) => {
      const url = `${type}?subject=Patient/${patientId}`;
      try {
        const bundle = await fhirClient.request(url, { pageLimit: 1, flat: false });
        const typeEntries = Array.isArray(bundle.entry) ? bundle.entry : [];

        typeEntries.forEach(({ resource }) => {
          if (!resource?.id) return;

          switch (type) {
            case "DocumentReference":
              if (Array.isArray(resource.content)) {
                resource.content.forEach((item, i) => {
                  const att = item.attachment;
                  results.push({
                    title:
                      att?.title || att?.description || resource.description || `DocumentReference ${resource.id}`,
                    sourceType: "DocumentReference",
                    resourceId: `${resource.id}-${i}`,
                    contentType: att?.contentType,
                    url: att?.url || null,
                    resource,
                  });
                });
              }
              break;

            case "DiagnosticReport":
              if (Array.isArray(resource.presentedForm)) {
                resource.presentedForm.forEach((pf, i) => {
                  results.push({
                    title: pf.title || resource.code?.text || `DiagnosticReport ${resource.id}`,
                    sourceType: "DiagnosticReport",
                    resourceId: `${resource.id}-${i}`,
                    contentType: pf.contentType,
                    url: pf.url || null,
                    resource,
                  });
                });
              }
              results.push({
                title: resource.code?.text || `DiagnosticReport ${resource.id}`,
                sourceType: "DiagnosticReport",
                resourceId: resource.id,
                resource,
              });
              break;

            case "Media":
              const att = resource.content?.attachment || resource.content;
              results.push({
                title: att?.title || `Media ${resource.id}`,
                sourceType: "Media",
                resourceId: resource.id,
                contentType: att?.contentType,
                url: att?.url || null,
                resource,
              });
              break;

            case "Observation":
              results.push({
                title: resource.code?.text || `Observation ${resource.id}`,
                sourceType: "Observation",
                resourceId: resource.id,
                resource,
              });
              break;


            case "Specimen":
              const typeText =
                resource.type?.text ||
                resource.type?.coding?.[0]?.display ||
                null;

              const subjectDisplay = resource.subject?.display || null;

              const title =
                typeText ||
                (subjectDisplay ? `Specimen from ${subjectDisplay}` : null) ||
                `Specimen ${resource.id}`;

              results.push({
                title,
                sourceType: "Specimen",
                resourceId: resource.id,
                resource,
              });
              break;

            case "CarePlan":
              results.push({
                title: resolveName(resource, fhirClient) || `CarePlan ${resource.id}`,
                sourceType: "CarePlan",
                resourceId: resource.id,
                resource,
              });
              break;

            case "Condition":
              results.push({
                title: resolveName(resource, fhirClient) || `Condition ${resource.id}`,
                sourceType: "Condition",
                resourceId: resource.id,
                resource,
              });
              break;

            case "QuestionnaireResponse":
              results.push({
                title: resolveName(resource, fhirClient) || `QuestionnaireResponse ${resource.id}`,
                sourceType: "QuestionnaireResponse",
                resourceId: resource.id,
                resource,
              });
              break;

            default:
              break;
          }
        });
      } catch (err) {
        console.error(`Error fetching ${type}:`, err);
      }
    });

    await Promise.all(fetchers);

    const sortedResults = results.sort((a, b) => getResourceDate(b) - getResourceDate(a));
    setEntries(sortedResults);
    setLoading(false);
  };

  useEffect(() => {
    fetchEntries();
  }, [patientId, fhirClient]);

  const toggleSelected = (item) => {
    const { resourceId } = item;
    setSelectedAttachments((prev) => {
      const exists = prev.some((a) => a.resourceId === resourceId);
      return exists ? prev.filter((a) => a.resourceId !== resourceId) : [...prev, item];
    });
  };

  const removeAttachment = (resourceId) => {
    setSelectedAttachments((prev) => prev.filter((a) => a.resourceId !== resourceId));
  };

  const attachAndClose = () => {
    onAttach?.((prev) => [...prev, ...selectedAttachments]);
    onClose?.();
  };

  const handleLocalFiles = (event) => {
    const files = Array.from(event.target.files).map((file, index) => ({
      title: file.name,
      fileType: "LocalFile",
      sourceType: "LocalFiles",
      resourceId: `local-${file.name}-${Date.now()}-${index}`,
      contentType: file.type,
      url: URL.createObjectURL(file),
      attachment: file,
    }));
    setLocalFiles((prev) => [...prev, ...files]);
  };

  const byType = (type) => entries.filter((e) => e.sourceType === type);
  const isChecked = (resourceId) => selectedAttachments.some((a) => a.resourceId === resourceId);

  return (
    <Container fluid className="p-0">
      {/* Attach Button */}
      <Row className="mb-2">
        <Col>
          <Button
            variant="primary"
            onClick={attachAndClose}
            disabled={selectedAttachments.length === 0}
          >
            Attach and Close ({selectedAttachments.length})
          </Button>
        </Col>
      </Row>

      {/* Selected Attachments */}
      <Row className="mb-3 align-items-start">
        <Col xs="auto" className="pe-2 fw-bold">
          Attachments:
        </Col>
        <Col>
          <div className="attachmentPillsContainer d-flex flex-wrap">
            {selectedAttachments.map(({ sourceType, resourceId, title, contentType }) => (
              <Badge
                key={resourceId}
                bg="info"
                className="me-2 mb-1 d-flex align-items-center"
                style={{ cursor: "pointer" }}
                onClick={() => removeAttachment(resourceId)}
              >
                {getIconForContentType(contentType || sourceType, "Browser")}
                <span className="ms-1">{truncate(title)}</span>
                <span className="ms-1 fw-bold">×</span>
              </Badge>
            ))}
          </div>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <Tabs
            id="attachments-tabs"
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
            className="mb-3"
          >
            {/* Resources Tab */}
            <Tab eventKey="resources" title="Resources">
              {loading ? (
                <div className="text-center p-4">
                  <Spinner animation="border" />
                  <p>Loading FHIR resources...</p>
                </div>
              ) : (
                <Accordion alwaysOpen>
                  {["Observation", "DocumentReference", "DiagnosticReport", "Media", "Specimen", "CarePlan", "Condition", "QuestionnaireResponse"].map((type, idx) => (
                    <Accordion.Item eventKey={`res-${idx}`} key={type}>
                      <Accordion.Header>
                        <span className={attachmentStyles.headerIcon}>
                          {getResourceIcon(type)}
                        </span>
                        <span className="ms-2">
                          {pluralize(type)} ({byType(type).length})
                        </span>
                      </Accordion.Header>
                      <Accordion.Body>
                        {byType(type).length > 0 ? (
                          <ListGroup>
                            {byType(type).map((entry) => (
                              <ListGroup.Item
                                key={entry.resourceId}
                                className={`${attachmentStyles.attachmentRow} d-flex justify-content-between align-items-center`}
                              >
                                {/* Left side: checkbox + title */}
                                <div
                                  className="d-flex align-items-center flex-grow-1"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => {
                                    // Row click: open extended card and also show attachments (current behavior)
                                    setSelectedEntry(entry); // open the extended card
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    className={attachmentStyles.checkbox}
                                    checked={isChecked(entry.resourceId)}
                                    onChange={() => toggleSelected(entry)}
                                    onClick={(e) => e.stopPropagation()} // stop checkbox click from triggering row click
                                  />
                                  <div className={attachmentStyles.title}>
                                    <strong>{entry.title}</strong>
                                    <div className={attachmentStyles.creation}>
                                      {formatLocalDate(
                                        entry.resource?.effectiveDateTime ||
                                        entry.resource?.issued ||
                                        entry.resource?.date ||
                                        "N/A"
                                      )}
                                    </div>
                                  </div>
                                </div>

                                {/* Right side: icon triggers separate behavior */}
                                <div
                                  className={`ms-2 ${attachmentStyles.attachmentRowIcon}`}
                                  style={{ cursor: "pointer" }}
                                  onClick={(e) => {
                                    console.log(entry.resource);
                                    e.stopPropagation(); // prevent row click behavior
                                    openGenericViewer(entry.resource, getExpandedIconFor(entry.sourceType));

                                  }}
                                >
                                  {getResourceIcon(entry.sourceType)}
                                </div>

                              </ListGroup.Item>
                            ))}
                          </ListGroup>
                        ) : (
                          <p>No {pluralize(type)} found.</p>
                        )}
                      </Accordion.Body>
                    </Accordion.Item>
                  ))}
                </Accordion>
              )}
            </Tab>

            {/* Local Files Tab */}
            <Tab eventKey="files" title="Files">
              <Accordion alwaysOpen>
                <Accordion.Item eventKey="files-local">
                  <Accordion.Header>
                    Local Files ({localFiles.length})
                  </Accordion.Header>
                  <Accordion.Body>
                    <input type="file" multiple onChange={handleLocalFiles} className="mb-2" />
                    {localFiles.length > 0 ? (
                      <ListGroup>
                        {localFiles.map((file) => (
                          <ListGroup.Item
                            key={file.resourceId}
                            className={`${attachmentStyles.attachmentRow} d-flex justify-content-between align-items-center`}
                          >
                            <div className="d-flex align-items-center">
                              <input
                                type="checkbox"
                                className={attachmentStyles.checkbox}
                                checked={isChecked(file.resourceId)}
                                onChange={() => toggleSelected(file)}
                              />
                              <div className={attachmentStyles.title}>{file.title}</div>
                            </div>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    ) : (
                      <p>No local files added.</p>
                    )}
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </Tab>
          </Tabs>
        </Col>

        <Col md={6}>
          <div
            className="bg-light p-3 rounded"
            style={{ maxHeight: "60vh", overflowY: "auto" }}
          >
            <AttachmentPreview
              entry={selectedEntry}
              selectedAttachments={selectedAttachments}
              toggleSelected={toggleSelected}
              fhirClient={fhirClient}
            />
          </div>
        </Col>
      </Row>

      <ResourceViewerModal
        show={showModal}
        onClose={handleModalClose}
        resource={modalResourceDetails}
        fhirClient={fhirClient}
      />

    </Container>
  );
};

export default AttachmentBrowser;
